/* Native collection links remain usable without JavaScript. Only results are replaced. */
if (!customElements.get('ip-kitchen-products')) {
  customElements.define('ip-kitchen-products', class extends HTMLElement {
    connectedCallback() {
      this.results = this.querySelector('[data-ip-kitchen-results]');
      this.status = this.querySelector('[data-ip-kitchen-status]');
      this.error = this.querySelector('[data-ip-kitchen-error]');
      if (!this.results || !this.dataset.sectionId) return;

      this.loadedUrl = this.pageUrl(location.href).href;
      this.onClick = this.handleClick.bind(this);
      this.onPopstate = () => {
        const url = this.pageUrl(location.href);
        const collectionPath = this.dataset.collectionUrl;
        if (url.pathname === collectionPath || url.pathname.startsWith(`${collectionPath}/`)) {
          if (url.href !== this.loadedUrl) this.load(url, { history: false });
          else this.cancel();
        }
      };
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopstate);
    }

    disconnectedCallback() {
      this.request?.abort();
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopstate);
    }

    pageUrl(href) {
      const url = new URL(href, location.href);
      const current = new URL(location.href);
      for (const key of ['preview_theme_id', 'view']) {
        if (!url.searchParams.has(key) && current.searchParams.has(key)) {
          url.searchParams.set(key, current.searchParams.get(key));
        }
      }
      url.searchParams.delete('section_id');
      url.hash = '';
      return url;
    }

    handleClick(event) {
      const link = event.target.closest('.ip-kitchen-filters__link, .ip-kitchen-pagination a');
      if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
      const url = this.pageUrl(link.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      // An active filter can also cancel a slower in-flight selection.
      if (url.href === this.loadedUrl) {
        this.cancel();
        return;
      }
      this.load(url, { history: true, paginate: Boolean(link.closest('.ip-kitchen-pagination')) });
    }

    setBusy(busy) {
      this.results.setAttribute('aria-busy', String(busy));
      this.results.inert = busy;
    }

    cancel() {
      this.request?.abort();
      this.request = null;
      this.setBusy(false);
      this.status.textContent = '';
      this.error.hidden = true;
    }

    async load(url, options) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 15000);
      this.error.hidden = true;
      this.status.textContent = 'Loading products…';
      this.setBusy(true);

      try {
        const sectionUrl = new URL(url);
        sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(sectionUrl, { signal: request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Products could not be loaded');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = [...document.querySelectorAll('ip-kitchen-products')].find(element => element.dataset.sectionId === this.dataset.sectionId);
        const results = section?.querySelector('[data-ip-kitchen-results]');
        if (!results?.querySelector('.ip-kitchen-products__grid')) throw new Error('Missing product results');
        if (request !== this.request || !this.isConnected) return;

        // Keep the filter DOM/focus and everything outside the grid untouched.
        this.results.replaceChildren(...results.childNodes);
        const activePaths = [...section.querySelectorAll('.ip-kitchen-filters__link[aria-current]')].map(link => new URL(link.getAttribute('href'), location.origin).pathname);
        this.querySelectorAll('.ip-kitchen-filters__link').forEach(link => {
          if (activePaths.includes(new URL(link.href).pathname)) link.setAttribute('aria-current', 'page');
          else link.removeAttribute('aria-current');
        });
        this.loadedUrl = url.href;
        if (options.history) {
          const historyUrl = new URL(url);
          historyUrl.hash = this.id;
          window.history.pushState(null, '', historyUrl);
        }
        this.status.textContent = `${this.querySelector('.ip-kitchen-filters__link[aria-current]')?.textContent.trim() || 'Products'}: ${this.querySelector('[data-results-label]').dataset.resultsLabel}`;

        // Pagination starts at the new products; category changes never move the page.
        if (options.paginate) {
          this.setBusy(false);
          this.querySelector('.ip-kitchen-products__grid').focus({ preventScroll: true });
          this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      } catch (error) {
        if (request !== this.request || !this.isConnected) return;
        this.querySelector('[data-ip-kitchen-fallback]').href = `${url.href}#${this.id}`;
        this.error.hidden = false;
        this.status.textContent = '';
      } finally {
        clearTimeout(timeout);
        if (request === this.request) {
          this.request = null;
          this.setBusy(false);
        }
      }
    }
  });
}
